import { makeAutoObservable } from 'mobx'
import { type INormalizedApiError } from 'shared/api'
import { AgentActionSendMessage } from 'entities/AgentActions'
import { RequestAttachment } from 'entities/Attachment/model/RequestAttachment'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { AgentActionMessageStore } from 'widgets/AgentActions'
import { SurveyMessageActionsStore } from 'pages/surveys/ui/SurveyView/store/surveyMessageActionsStore'

export type ICategoryMessageValue = {
  message: string
  media_url: RequestAttachment[]
}

export class CategoryMessageStore {
  isAdded = false
  error = ''
  readonly model: AgentActionSendMessage
  readonly messageStore: AgentActionMessageStore
  private _messageActions: SurveyMessageActionsStore | null = null

  constructor(private _rootKey: string, entities: IEntitiesStore) {
    this.model = AgentActionSendMessage.Create()
    this.messageStore = new AgentActionMessageStore(this.model, entities, {
      makeActions: ({ messageFieldStore, savedRepliesStore }) => {
        this._messageActions = new SurveyMessageActionsStore(
          messageFieldStore,
          savedRepliesStore,
          entities
        )
        return this._messageActions.actions
      },
    })
    makeAutoObservable(this)
  }

  get hasMessage() {
    return this.isAdded && (Boolean(this.model.message.trim()) || this.model.attachments.length > 0)
  }

  get previewHtml() {
    return this.model.message ? this.messageStore.message.messageHtml : ''
  }

  get previewAttachments() {
    return this.messageStore.message.attachments
  }

  get paramsValue(): ICategoryMessageValue | null {
    if (!this.hasMessage) return null
    return {
      message: this.model.message,
      media_url: this.model.attachments.map((attachment) => new RequestAttachment(attachment)),
    }
  }

  init = (value: ICategoryMessageValue | null | undefined) => {
    this.model.syncOrigin({ message: value?.message ?? '', media_url: value?.media_url ?? [] })
    this.messageStore.syncMessage()
    this.isAdded = Boolean(value?.message) || (value?.media_url?.length ?? 0) > 0
    this.error = ''
  }

  add = () => {
    this.isAdded = true
  }

  remove = () => {
    this.isAdded = false
    this.model.sync('', [])
    this.messageStore.syncMessage()
    this.setError('')
  }

  setError = (value: string) => {
    this.error = value
  }

  validate = (emptyMessageError: string) => {
    if (this.isAdded && this.model.attachments.length > 0 && !this.model.message.trim()) {
      this.setError(emptyMessageError)
      return false
    }
    this.setError('')
    return true
  }

  syncApiErrors = (apiErrors: INormalizedApiError | null | undefined) => {
    const match = apiErrors?.entries().find(([key]) => key.includes(this._rootKey))
    this.setError(match?.[1]?.value ?? '')
  }

  dispose = () => {
    this._messageActions?.dispose()
    this.messageStore.dispose()
  }
}
